import * as React from 'react';
import { cn } from '../utils/addToClassName';
import { StrictOmit } from '../utils/strictOmit';

/**
 * The image component is a wrapper around the HTML image that adds the
 * functionality of adding image crops using the sizes and srcSet props.
 */

export type ImageCrop = { width: number; url: string }
export type CroppedImageURLs = { src: string; crops: ImageCrop[] }
export interface ImgProps
  extends StrictOmit<React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement, HTMLImageElement>, HTMLImageElement>, 'srcSet'> {
  crops?: ImageCrop[]
  loading?: 'lazy' | 'eager'
  fetchPriority?: 'high' | 'low' | 'auto'
}

export const Img = React.forwardRef<HTMLImageElement, ImgProps>(
  ({ crops, src, alt, sizes, className, onLoad, ...props }, ref) => {
    const [isLoaded, setIsLoaded] = React.useState(false)

    return (
      <img
        {...props}
        ref={ref}
        alt={alt || ''}
        {...{ loading: props.loading || undefined }}
        {...{ fetchPriority: props.fetchPriority || undefined }}
        src={crops && crops.length > 0 ? crops[0].url : src}
        className={cn(isLoaded ? 'loaded' : 'loading', className)}
        /**
         * The image sizes props needs to be set individually in every context
         * an image is used such as a modern block.
         */
        srcSet={crops && crops.length > 0 ? getSrcSet(crops) : undefined}
        sizes={crops && crops.length > 0 ? sizes || DEFAULT_IMG_SIZES : undefined}
        onLoad={(e) => {
          setIsLoaded(true)
          if (onLoad) onLoad(e)
        }}
      />
    )
  }
)

export const getSrcSet = (crops: ImageCrop[]) => crops.map(({ url, width }) => `${url} ${width}w`).join(', ')

export const DEFAULT_IMG_SIZES = '100vw'
